import { Controller } from "@hotwired/stimulus"
import { TRACKING_CONSENT_ACCEPTED_EVENT, track, trackingAllowed } from "../lib/analytics"

/**
 * Smart survey prompt — slide-up banner + modal overlay.
 *
 * Mobile: bottom sheet sliding up
 * Desktop: centered modal with backdrop
 *
 * Surveys arrive from the server (sorted by priority DESC) with
 * per-survey config:
 *   - trigger_type: idle | time_on_page | pageviews | video_dwell | manual
 *   - trigger_seconds: delay for idle / time_on_page / video_dwell
 *   - trigger_pageview_count: threshold for pageviews
 *   - path_pattern: glob like "/cars/*" or "/" (nil = all paths)
 *   - frequency_cap: unlimited | once_ever | once_per_week | once_per_month
 *   - target_type + target_car_id for car-scoped surveys
 *
 * Selection: highest-priority survey whose gates all pass; then only ITS
 * trigger is armed. No fallthrough to lower-priority surveys in the same
 * session.
 */
const SESSION_PAGEVIEW_KEY = "ford_survey_pageviews"
const DEFAULT_IDLE_MS = 30_000
const DEFAULT_TIME_ON_PAGE_MS = 10_000
const DEFAULT_VIDEO_MS = 15_000
const DEFAULT_PAGEVIEW_THRESHOLD = 3


export default class extends Controller {
  static targets = ["banner", "title", "overlay", "panel", "modalTitle", "modalContent"]
  static values = { surveys: Array, carId: String }

  connect() {
    this.onTrackingConsentAccepted = this.onTrackingConsentAccepted.bind(this)
    document.addEventListener(TRACKING_CONSENT_ACCEPTED_EVENT, this.onTrackingConsentAccepted)
    if (!trackingAllowed()) return
    this.start()
  }

  start() {
    if (this.started) return
    this.started = true
    this.triggered = false
    this.lastHandledUrl = null
    this.nextSurveys = undefined
    this.nextCarId = undefined

    // Bind once so add/remove match
    this.onTurboLoad = this.onTurboLoad.bind(this)
    this.onTurboBeforeRender = this.onTurboBeforeRender.bind(this)
    this.onTurboBeforeCache = this.onTurboBeforeCache.bind(this)
    this.onActivity = this.onActivity.bind(this)

    document.addEventListener("turbo:load", this.onTurboLoad)
    document.addEventListener("turbo:before-render", this.onTurboBeforeRender)
    // Strip modal-open state before Turbo snapshots the page for its
    // cache. Without this, navigating away mid-modal bakes
    // body.survey-modal-open into the snapshot, and a back-nav restores
    // it — hiding FORDI on every subsequent page until manually cleared.
    document.addEventListener("turbo:before-cache", this.onTurboBeforeCache)
    const passive = { passive: true }
    document.addEventListener("mousemove", this.onActivity, passive)
    document.addEventListener("keydown", this.onActivity, passive)
    document.addEventListener("scroll", this.onActivity, passive)
    document.addEventListener("touchstart", this.onActivity, passive)

    this.handlePageArrival()
  }

  disconnect() {
    document.removeEventListener(TRACKING_CONSENT_ACCEPTED_EVENT, this.onTrackingConsentAccepted)
    if (!this.started) return

    document.removeEventListener("turbo:load", this.onTurboLoad)
    document.removeEventListener("turbo:before-render", this.onTurboBeforeRender)
    document.removeEventListener("turbo:before-cache", this.onTurboBeforeCache)
    document.removeEventListener("mousemove", this.onActivity)
    document.removeEventListener("keydown", this.onActivity)
    document.removeEventListener("scroll", this.onActivity)
    document.removeEventListener("touchstart", this.onActivity)
    this.clearAllTimers()
    document.body.style.overflow = ""
    // Defensive: if the controller disconnects while the modal is open
    // (e.g. Turbo navigation), make sure we don't leave the body class
    // on — it would permanently hide the Botmaker chat.
    document.body.classList.remove("survey-modal-open")
  }

  onTrackingConsentAccepted() {
    this.start()
  }

  // ── Trigger pipeline ──

  onTurboLoad() {
    this.handlePageArrival()
  }

  onTurboBeforeRender(event) {
    const freshRoot = event.detail?.newBody?.querySelector("#survey-prompt-root")

    if (!freshRoot) {
      this.nextSurveys = []
      this.nextCarId = ""
      return
    }

    this.nextSurveys = this.parseSurveys(freshRoot.dataset.surveyPromptSurveysValue)
    this.nextCarId = freshRoot.dataset.surveyPromptCarIdValue || ""
  }

  onTurboBeforeCache() {
    // Reset modal state so the cached snapshot is clean. If the user
    // navigates away with the modal open, we still want FORDI visible
    // when they come back via the back button.
    document.body.classList.remove("survey-modal-open")
    document.body.style.overflow = ""
    if (this.hasOverlayTarget) this.overlayTarget.classList.add("hidden")
    if (this.hasPanelTarget) {
      this.panelTarget.classList.add("translate-y-full", "md:scale-95", "md:opacity-0")
      this.panelTarget.classList.remove("translate-y-0", "md:scale-100", "md:opacity-100")
    }
  }

  handlePageArrival() {
    // Stale-state guard: body.survey-modal-open is meant to be on ONLY
    // while our overlay is visible. If a previous nav (browser back,
    // closed tab, bfcache restore) left it stuck — and the survey-prompt
    // root is data-turbo-permanent so disconnect() never fires on
    // Turbo nav — Botmaker (FORDI) stays display:none on every page.
    // Reset before anything else.
    if (document.body.classList.contains("survey-modal-open")) {
      const overlayHidden = !this.hasOverlayTarget ||
                            this.overlayTarget.classList.contains("hidden")
      if (overlayHidden) {
        document.body.classList.remove("survey-modal-open")
        document.body.style.overflow = ""
      }
    }

    this.refreshPageValues()

    if (this.triggered) return

    const currentUrl = window.location.pathname + window.location.search
    if (this.lastHandledUrl === currentUrl) return
    this.lastHandledUrl = currentUrl

    // Bump session pageview counter BEFORE picking — pageviews trigger
    // needs the up-to-date count.
    const count = parseInt(sessionStorage.getItem(SESSION_PAGEVIEW_KEY) || "0", 10) + 1
    sessionStorage.setItem(SESSION_PAGEVIEW_KEY, String(count))

    // Re-pick per page: path_pattern may differ page-to-page.
    this.selectedSurvey = this.pickSurvey()
    if (!this.selectedSurvey) return

    this.clearAllTimers()
    this.armTrigger(this.selectedSurvey, count)
  }

  armTrigger(survey, pageviewCount) {
    const seconds = survey.trigger_seconds
    switch (survey.trigger_type) {
      case "time_on_page": {
        const ms = (seconds || DEFAULT_TIME_ON_PAGE_MS / 1000) * 1000
        this.pageTimer = setTimeout(() => this.fire("time_on_page"), ms)
        break
      }
      case "pageviews": {
        const threshold = survey.trigger_pageview_count || DEFAULT_PAGEVIEW_THRESHOLD
        if (pageviewCount >= threshold) this.fire("pageviews")
        break
      }
      case "video_dwell": {
        if (document.querySelector(".ford-video-player")) {
          const ms = (seconds || DEFAULT_VIDEO_MS / 1000) * 1000
          this.videoTimer = setTimeout(() => this.fire("video_dwell"), ms)
        }
        break
      }
      case "manual":
        // Never auto-triggers. Kept for future explicit JS triggers.
        break
      case "idle":
      default: {
        this.idleMs = (seconds || DEFAULT_IDLE_MS / 1000) * 1000
        this.resetIdleTimer()
        break
      }
    }
  }

  onActivity() {
    if (this.triggered) return
    if (!this.selectedSurvey || this.selectedSurvey.trigger_type !== "idle") return
    this.resetIdleTimer()
  }

  resetIdleTimer() {
    if (this.triggered) return
    clearTimeout(this.idleTimer)
    this.idleTimer = setTimeout(() => this.fire("idle"), this.idleMs || DEFAULT_IDLE_MS)
  }

  fire(_reason) {
    if (this.triggered) return
    this.triggered = true
    this.clearAllTimers()
    this.showBanner()
  }

  clearAllTimers() {
    clearTimeout(this.idleTimer)
    clearTimeout(this.videoTimer)
    clearTimeout(this.pageTimer)
  }

  // ── Actions ──

  dismiss() {
    if (!this.selectedSurvey) return
    this.recordImpression()
    this.hideBanner()
  }

  async openModal(event) {
    event.preventDefault()
    if (!this.selectedSurvey) return

    this.hideBanner()
    this.modalTitleTarget.textContent = this.selectedSurvey.title || ""
    this.surveyCompleted = false
    track("survey_step", { survey_id: this.selectedSurvey.id, step: 0, action: "start" })

    this.overlayTarget.classList.remove("hidden")
    document.body.style.overflow = "hidden"
    document.body.classList.add("survey-modal-open")

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.panelTarget.classList.remove("translate-y-full", "md:scale-95", "md:opacity-0")
        this.panelTarget.classList.add("translate-y-0", "md:scale-100", "md:opacity-100")
      })
    })

    const url = this.surveyUrl(this.selectedSurvey, { modal: "true" })
    try {
      const resp = await fetch(url, { headers: { "X-Survey-Modal": "true" } })
      const html = await resp.text()
      this.modalContentTarget.innerHTML = html
      this.bindModalForm()
    } catch {
      this.closeModal()
      window.location.href = this.surveyUrl(this.selectedSurvey)
    }
  }

  closeModal() {
    this.panelTarget.classList.add("translate-y-full", "md:scale-95", "md:opacity-0")
    this.panelTarget.classList.remove("translate-y-0", "md:scale-100", "md:opacity-100")

    // Restore the Botmaker chat immediately — the 300ms delay was only
    // there for the panel slide-out animation, not for chat visibility.
    document.body.style.overflow = ""
    document.body.classList.remove("survey-modal-open")

    setTimeout(() => {
      this.overlayTarget.classList.add("hidden")
      this.modalContentTarget.innerHTML = ""
    }, 300)

    // If the user closed before submitting, treat it as an abandon.
    if (this.selectedSurvey && !this.surveyCompleted) {
      track("survey_step", { survey_id: this.selectedSurvey.id, step: 0, action: "abandon" })
    }

    this.recordImpression()
  }

  // ── Internal ──

  refreshPageValues() {
    if (this.nextSurveys !== undefined) {
      this.surveysValue = this.nextSurveys
      this.nextSurveys = undefined
    }

    if (this.nextCarId !== undefined) {
      this.carIdValue = this.nextCarId
      this.nextCarId = undefined
    }
  }

  parseSurveys(value) {
    if (!value) return []

    try {
      const parsed = JSON.parse(value)
      return Array.isArray(parsed) ? parsed : []
    } catch {
      return []
    }
  }

  async handleSubmit(event) {
    event.preventDefault()
    const form = event.target

    if (!this.validateForm(form)) return

    const submitBtn = form.querySelector('button[type="submit"]')

    if (submitBtn) {
      submitBtn.disabled = true
      submitBtn.style.opacity = "0.6"
    }

    try {
      const resp = await fetch(form.action, {
        method: "POST",
        headers: { "X-Survey-Modal": "true" },
        body: new FormData(form)
      })
      const html = await resp.text()
      this.modalContentTarget.innerHTML = html

      if (!resp.ok) {
        this.bindModalForm()
        return
      }

      const thankYou = this.modalContentTarget.querySelector("[data-survey-id]")
      if (thankYou) {
        this.markCompleted(thankYou.dataset.surveyId)
        this.surveyCompleted = true
        if (this.selectedSurvey) {
          track("survey_step", { survey_id: this.selectedSurvey.id, step: 1, action: "complete" })
        }
      }
    } catch {
      this.closeModal()
    }
  }

  bindModalForm() {
    const form = this.modalContentTarget.querySelector("form")
    if (!form) return

    form.addEventListener("submit", (e) => this.handleSubmit(e))
    form.addEventListener("input", (e) => this.clearQuestionForControl(e.target))
    form.addEventListener("change", (e) => this.clearQuestionForControl(e.target))
  }

  validateForm(form) {
    this.clearFormErrors(form)

    const invalidQuestions = Array.from(
      form.querySelectorAll('[data-survey-question][data-survey-required="true"]')
    ).filter((question) => !this.questionHasAnswer(question))

    if (invalidQuestions.length === 0) return true

    const message = form.dataset.requiredMessage || "Responda todas as perguntas obrigatórias."
    this.showFormError(form, message)
    invalidQuestions.forEach((question) => this.markQuestionInvalid(form, question))

    const firstInvalid = invalidQuestions[0]
    firstInvalid.scrollIntoView({ behavior: "smooth", block: "center" })

    const focusable = firstInvalid.querySelector("textarea, input:not([type='hidden']), select")
    if (focusable && focusable.offsetParent !== null) {
      focusable.focus({ preventScroll: true })
    }

    return false
  }

  questionHasAnswer(question) {
    const controls = Array.from(question.querySelectorAll("input, textarea, select"))
      .filter((control) => !control.disabled)

    return controls.some((control) => {
      if (control.type === "checkbox" || control.type === "radio") return control.checked
      return String(control.value || "").trim().length > 0
    })
  }

  clearFormErrors(form) {
    const formError = form.querySelector("[data-survey-form-error]")
    if (formError) {
      formError.classList.add("hidden")
      formError.textContent = ""
    }

    form.querySelectorAll("[data-survey-question]").forEach((question) => {
      this.clearQuestionError(question)
    })
  }

  clearQuestionForControl(control) {
    const question = control.closest?.("[data-survey-question]")
    if (!question || !this.questionHasAnswer(question)) return
    this.clearQuestionError(question)
  }

  markQuestionInvalid(form, question) {
    question.classList.add("rounded-lg", "bg-red-50/60", "p-3", "-mx-3", "ring-1", "ring-red-200")

    const questionError = question.querySelector("[data-survey-question-error]")
    if (!questionError) return

    questionError.textContent = form.dataset.questionRequiredMessage || "Campo obrigatório."
    questionError.classList.remove("hidden")
  }

  clearQuestionError(question) {
    question.classList.remove("rounded-lg", "bg-red-50/60", "p-3", "-mx-3", "ring-1", "ring-red-200")

    const questionError = question.querySelector("[data-survey-question-error]")
    if (questionError) {
      questionError.classList.add("hidden")
      questionError.textContent = ""
    }
  }

  showFormError(form, message) {
    const formError = form.querySelector("[data-survey-form-error]")
    if (!formError) return

    formError.textContent = message
    formError.classList.remove("hidden")
  }

  // ── Banner ──

  showBanner() {
    if (!this.hasBannerTarget) return
    this.titleTarget.textContent = this.selectedSurvey.title || ""
    this.bannerTarget.classList.add("ford-survey-banner--visible")
    // Record a "last_shown_at" as soon as the user sees the banner so
    // frequency caps don't require a dismiss click.
    const data = this.getData(this.selectedSurvey.id)
    data.last_shown_at = Date.now()
    localStorage.setItem(this.key(this.selectedSurvey.id), JSON.stringify(data))
  }

  hideBanner() {
    if (!this.hasBannerTarget) return
    this.bannerTarget.classList.remove("ford-survey-banner--visible")
  }

  // ── Gate logic ──

  pickSurvey() {
    // surveysValue is pre-sorted by priority DESC (server-side).
    return (this.surveysValue || []).find(s => this.shouldShow(s)) || null
  }

  shouldShow(survey) {
    const data = this.getData(survey.id)
    if (data.completed) return false

    // Frequency cap (based on last_shown_at, set when the banner renders).
    // `unlimited` intentionally has no impression cap and no backoff —
    // it means *show every time the trigger conditions match*. The
    // previous behavior (3 impressions max with 0d/3d/7d backoff) was a
    // legacy default from before once_per_week/month existed and didn't
    // match what admins expected when they picked "Ilimitada".
    const cap = survey.frequency_cap || "unlimited"
    if (cap === "once_ever" && data.last_shown_at) return false
    if (data.last_shown_at) {
      const daysSince = (Date.now() - data.last_shown_at) / (1000 * 60 * 60 * 24)
      if (cap === "once_per_week"  && daysSince < 7)  return false
      if (cap === "once_per_month" && daysSince < 30) return false
    }

    // Path pattern: `*` wildcard, empty = all pages.
    if (survey.path_pattern && !this.pathMatches(survey.path_pattern)) return false

    // Car targeting.
    if (survey.target_type === "car") {
      if (!this.carIdValue || this.carIdValue !== survey.target_car_id) return false
    }

    return true
  }

  pathMatches(pattern) {
    const path = window.location.pathname
    const escaped = pattern.replace(/[.+?^${}()|[\]\\]/g, "\\$&").replace(/\*/g, ".*")
    return new RegExp(`^${escaped}$`).test(path)
  }

  recordImpression() {
    if (!this.selectedSurvey) return
    const data = this.getData(this.selectedSurvey.id)
    data.impressions = (data.impressions || 0) + 1
    data.dismissed_at = Date.now()
    localStorage.setItem(this.key(this.selectedSurvey.id), JSON.stringify(data))
  }

  surveyUrl(survey, params = {}) {
    const url = new URL(`/surveys/${survey.slug}`, window.location.origin)
    if (survey.target_type === "car" && this.carIdValue) {
      url.searchParams.set("car_id", this.carIdValue)
    }
    Object.entries(params).forEach(([key, value]) => url.searchParams.set(key, value))
    return `${url.pathname}${url.search}`
  }

  markCompleted(id) {
    const data = this.getData(id)
    data.completed = true
    localStorage.setItem(this.key(id), JSON.stringify(data))
  }

  getData(id) {
    try { return JSON.parse(localStorage.getItem(this.key(id)) || "{}") }
    catch { return {} }
  }

  key(id) { return `survey_${id}` }
}
